import { useMemo, useState, type ReactNode } from 'react';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp, slugForFile } from '../lib/download';
import { formatNumber, num, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

interface Source {
  figure: string;
  value: number;
  entity: string;
  fields: string;
  filter: string;
  formula: string;
  records: number;
  note?: string;
}
interface PlItem {
  date: string;
  name: string;
  description: string;
  quantity: number | null;
  price: number;
  cost: number | null;
}
interface Payload {
  company: { id: number; name: string; classification: string | null };
  from: string;
  to: string;
  fetchedUtc: string;
  totals: {
    revenue: number;
    revenueRecurring: number;
    revenueLabor: number;
    revenueProducts: number;
    hoursWorked: number;
    billableHours: number;
    nonBillableHours: number;
    laborCost: number;
    uncostedHours: number;
    costRecurring: number;
    costProducts: number;
    otherCost: number;
    totalCost: number;
    profit: number;
    marginPct: number | null;
    revenuePerHour: number | null;
    laborCostPerHour: number | null;
    laborDrawPct: number | null;
    invoiced: number;
    invoices: number;
    postedProfit: number;
    postedMarginPct: number | null;
    laborItemsOurCost: number;
  };
  months: {
    month: string;
    revenue: number;
    recurring: number;
    labor: number;
    products: number;
    hours: number;
    billableHours: number;
    laborCost: number;
    otherCost: number;
    invoiced: number;
    profit: number;
    marginPct: number | null;
    postedProfit: number;
    postedMarginPct: number | null;
  }[];
  invoiceList: { number: string; date: string; total: number }[];
  people: { name: string; internalCost: number | null; hours: number; billableHours: number; cost: number | null }[];
  contracts: { name: string; type: string | null; hours: number; billableHours: number; cost: number }[];
  items: Record<'recurring' | 'labor' | 'products', PlItem[]>;
  workTypes: { name: string; hours: number; billableHours: number; cost: number; uncostedHours: number }[];
  warnings: string[];
  sources: Source[];
}

const money = (v: number | null | undefined) => (v === null || v === undefined ? '—' : formatNumber(v, 'currency'));
const pct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);
const monthName = (k: string) => new Date(`${k}-01T00:00:00`).toLocaleDateString([], { month: 'short', year: 'numeric' });
const day = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });

/** Quick ranges, as local YYYY-MM-DD, to inclusive. */
function quick(k: '12m' | 'ytd' | 'lastYear' | 'lastQuarter'): { from: string; to: string } {
  const d = new Date();
  if (k === '12m') return { from: toLocalDateString(new Date(d.getFullYear(), d.getMonth() - 11, 1)), to: todayLocal() };
  if (k === 'ytd') return { from: toLocalDateString(new Date(d.getFullYear(), 0, 1)), to: todayLocal() };
  if (k === 'lastYear') return { from: toLocalDateString(new Date(d.getFullYear() - 1, 0, 1)), to: toLocalDateString(new Date(d.getFullYear() - 1, 11, 31)) };
  const q = Math.floor(d.getMonth() / 3);
  return { from: toLocalDateString(new Date(d.getFullYear(), (q - 1) * 3, 1)), to: toLocalDateString(new Date(d.getFullYear(), q * 3, 0)) };
}
const QUICK: { k: Parameters<typeof quick>[0]; label: string }[] = [
  { k: '12m', label: 'Last 12 months' },
  { k: 'ytd', label: 'This year' },
  { k: 'lastYear', label: 'Last year' },
  { k: 'lastQuarter', label: 'Last quarter' },
];

/**
 * Reports › Client Profitability — one client's revenue, the labor it drew,
 * what it all cost, and the profit, for the dates chosen, live from Autotask
 * (routes/reportsProfitability.ts). Every figure links to "Where every number
 * comes from", which names the Autotask entity, fields, filter, formula and
 * record count behind it, so the right fields can be checked.
 */
export function ProfitabilityReportPage() {
  /* One client in full, or every client on one line each. */
  const [mode, setMode] = useState<'one' | 'all'>('one');
  const companies = useHeldResource<{ id: number; name: string }[]>(mode === 'one' ? '/reports/profitability/companies' : null);
  const [company, setCompany] = useState<{ id: number; name: string } | null>(null);
  const [search, setSearch] = useState('');
  const [{ from, to }, setRange] = useState(() => quick('12m'));
  /* Which headline figure is opened out, if any. */
  const [drill, setDrill] = useState<DrillKey | null>(null);
  const pickDrill = (k: DrillKey) => setDrill((p) => (p === k ? null : k));
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const data = useHeldResource<Payload>(company ? `/reports/profitability?${new URLSearchParams({ companyId: String(company.id), from, to })}` : null);
  const d = data.data;

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = companies.data ?? [];
    return (q ? list.filter((c) => c.name.toLowerCase().includes(q)) : list).slice(0, 60);
  }, [companies.data, search]);

  const apply = (r: { from: string; to: string }) => {
    setDraftFrom(r.from);
    setDraftTo(r.to);
    setRange(r);
  };

  const exportCsv = () => {
    if (!d) return;
    downloadCsv(`${slugForFile(d.company.name)}_profitability_${fileStamp()}.csv`, [
      ['Client', d.company.name],
      ['Classification', d.company.classification ?? ''],
      ['Period', d.from, d.to],
      [],
      ['Month', 'Recurring revenue', 'Labor revenue', 'Products revenue', 'Posted revenue', 'Invoiced', 'Hours worked', 'Billable hours', 'Labor cost', 'Other cost', 'Profit (invoiced − cost)', 'Margin %', 'Profit on posted', 'Posted margin %'],
      ...d.months.map((m) => [m.month, m.recurring, m.labor, m.products, m.revenue, m.invoiced, m.hours, m.billableHours, m.laborCost, m.otherCost, m.profit, m.marginPct ?? '', m.postedProfit, m.postedMarginPct ?? '']),
      [],
      ['Invoice', 'Date', 'Amount'],
      ...d.invoiceList.map((i) => [i.number, i.date, i.total]),
      [],
      ['Figure', 'Value', 'Autotask entity', 'Fields', 'Filter', 'Formula', 'Records', 'Note'],
      ...d.sources.map((s) => [s.figure, s.value, s.entity, s.fields, s.filter, s.formula, s.records, s.note ?? '']),
    ]);
  };

  const t = d?.totals;
  const src = (figure: string) => (
    <a href={`#src-${slugForFile(figure)}`} className="xs muted pf-src-link" title="Where this number comes from">
      source
    </a>
  );

  const modeSwitch = (
    <div className="row" style={{ gap: 'var(--space-1)' }} role="tablist" aria-label="View">
      <button type="button" role="tab" aria-selected={mode === 'one'} className={`btn btn-sm ${mode === 'one' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMode('one')}>
        One client
      </button>
      <button type="button" role="tab" aria-selected={mode === 'all'} className={`btn btn-sm ${mode === 'all' ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setMode('all')}>
        All clients
      </button>
    </div>
  );

  if (mode === 'all') {
    return (
      <>
        <PageHead
          title="Client Profitability"
          lede="Every active client on one line: revenue, labor draw, cost, profit and margin — red at 50% or below, green above. Click a client for its full breakdown and where every number came from."
          actions={modeSwitch}
        />
        <AllClients
          from={from}
          to={to}
          onRange={apply}
          onOpen={(c) => {
            setCompany(c);
            setMode('one');
          }}
        />
      </>
    );
  }

  return (
    <>
      <PageHead
        title="Client Profitability"
        lede="One client's revenue, the labor they drew, what it all cost and the profit — live from Autotask, with where every number came from so the fields can be checked."
        actions={modeSwitch}
      />

      <section className="card mb-4">
        <div className="card-body">
          {company ? (
            <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
              {/* Held at a readable width: squeezed, a long classification ran under the buttons. */}
              <div className="grow" style={{ flexBasis: 320 }}>
                <span className="eyebrow">Client</span>
                <h2 style={{ margin: 0 }}>{company.name}</h2>
                {d && d.company.id === company.id && (
                  <div className="row" style={{ gap: 'var(--space-2)', alignItems: 'center', marginTop: 'var(--space-1)' }}>
                    <span className="xs muted">Classification</span>
                    {d.company.classification ? (
                      <span
                        className="badge badge-neutral"
                        style={{ whiteSpace: 'normal' }}
                        title="Autotask company classification (Companies.classification)"
                      >
                        {d.company.classification}
                      </span>
                    ) : (
                      <span className="xs muted">None set in Autotask</span>
                    )}
                  </div>
                )}
              </div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCompany(null)}>
                Change client
              </button>
              <Field label="From">
                <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
              </Field>
              <Field label="To">
                <input className="input" type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
              </Field>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={!draftFrom || !draftTo || draftFrom > draftTo || (draftFrom === from && draftTo === to)}
                onClick={() => setRange({ from: draftFrom, to: draftTo })}
              >
                Show
              </button>
              <div className="row wrap" style={{ gap: 'var(--space-1)' }}>
                {QUICK.map((q) => (
                  <button key={q.k} type="button" className="btn btn-ghost btn-sm" onClick={() => apply(quick(q.k))}>
                    {q.label}
                  </button>
                ))}
              </div>
              <button type="button" className="btn btn-secondary btn-sm" disabled={!d} onClick={exportCsv}>
                Export CSV
              </button>
              <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
                {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
              </button>
            </div>
          ) : (
            <>
              <span className="eyebrow">Choose a client</span>
              {companies.error && <ErrorBanner message={companies.error} />}
              {companies.loading && <Loading label="Reading active companies from Autotask" />}
              {companies.data && (
                <>
                  <input
                    className="input mt-2"
                    autoFocus
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={`Type to filter ${companies.data.length.toLocaleString()} active companies`}
                  />
                  <div className="sdf-pick mt-2">
                    {matches.length === 0 && <p className="xs muted">No active company contains “{search}”.</p>}
                    {matches.map((c) => (
                      <button key={c.id} type="button" className="sdf-pick-row" onClick={() => setCompany(c)}>
                        {c.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {company && data.loading && !d && <Loading label="Reading billing, time and invoices from Autotask" />}

      {d && t && (
        <>
          <p className="xs muted mb-2">
            {day(d.from)} – {day(d.to)} · read {new Date(d.fetchedUtc).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
          </p>
          {d.warnings.length > 0 && (
            <div className="card mb-4 pf-warn">
              <div className="card-body small">
                {d.warnings.map((w) => (
                  <p key={w} style={{ margin: '0 0 4px' }}>
                    ⚠ {w}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Every figure opens the rows it was added up from, under the row
              it was clicked in, so the detail appears where you are looking. */}
          <div className="grid grid-4 mb-4">
            <DrillStat
              k="revenue"
              open={drill}
              onPick={pickDrill}
              label="Revenue"
              value={money(t.invoiced)}
              foot={`${num(t.invoices)} invoices · incl. tax, dated by invoice`}
            />
            <DrillStat
              k="hours"
              open={drill}
              onPick={pickDrill}
              label="Labor draw"
              value={`${num(t.hoursWorked, 1)} h`}
              foot={`${num(t.billableHours, 1)} billable · ${num(t.nonBillableHours, 1)} not`}
            />
            <DrillStat
              k="cost"
              open={drill}
              onPick={pickDrill}
              label="Total cost"
              value={money(t.totalCost)}
              foot={`${money(t.laborCost)} labor · ${money(t.otherCost)} other`}
            />
            <DrillStat
              k="profit"
              open={drill}
              onPick={pickDrill}
              label="Profit"
              value={money(t.profit)}
              foot={`${pct(t.marginPct)} margin · invoiced − cost`}
            />
          </div>
          {drill && ROW1.includes(drill) && <Drill k={drill} d={d} onClose={() => setDrill(null)} />}
          <div className="grid grid-4 mb-4">
            <DrillStat
              k="perHour"
              open={drill}
              onPick={pickDrill}
              label="Revenue per hour worked"
              value={money(t.revenuePerHour)}
              foot="invoiced ÷ hours worked"
            />
            <DrillStat
              k="costPerHour"
              open={drill}
              onPick={pickDrill}
              label="Labor cost per hour"
              value={money(t.laborCostPerHour)}
              foot="average internal cost"
            />
            <DrillStat
              k="draw"
              open={drill}
              onPick={pickDrill}
              label="Labor draw on recurring"
              value={pct(t.laborDrawPct)}
              foot="labor cost ÷ recurring revenue"
            />
            <DrillStat
              k="recurring"
              open={drill}
              onPick={pickDrill}
              label="Recurring revenue"
              value={money(t.revenueRecurring)}
              foot={`${money(t.costRecurring)} recurring cost`}
            />
          </div>
          {drill && !ROW1.includes(drill) && <Drill k={drill} d={d} onClose={() => setDrill(null)} />}

          <section className="card mb-4">
            <div className="card-head">
              <div className="grow">
                <h3>Profit and loss</h3>
                <p className="sub">
                  Posted billing items, dated by item — the only revenue that can be split by what was sold, so the P&amp;L runs on it rather than on
                  the {money(t.invoiced)} invoiced. Each line links to where it came from.
                </p>
              </div>
            </div>
            <div className="table-wrap">
              <table className="table pf-pl" data-nosort>
                <tbody>
                  <tr className="pf-section"><td colSpan={3}>Revenue</td></tr>
                  <PlLine label="Recurring services" value={t.revenueRecurring} source={src('Recurring revenue')} items={d.items?.recurring} showCost />
                  <PlLine label="Labor billed" value={t.revenueLabor} source={src('Labor revenue (billed time)')} items={d.items?.labor} />
                  <PlLine label="Products and expenses" value={t.revenueProducts} source={src('Products & expenses revenue')} items={d.items?.products} showCost />
                  <tr className="pf-sub"><td>Total revenue</td><td className="num">{money(t.revenue)}</td><td /></tr>
                  {/* In the same order as revenue, so each cost sits opposite what it earned. */}
                  <tr className="pf-section"><td colSpan={3}>Cost</td></tr>
                  <tr><td>Recurring services cost</td><td className="num">{money(t.costRecurring)}</td><td>{src('Recurring cost')}</td></tr>
                  <tr>
                    <td>Labor — {num(t.hoursWorked, 1)} hours worked at internal cost{t.uncostedHours > 0 && <span className="xs muted"> ({num(t.uncostedHours, 1)} h not costed)</span>}</td>
                    <td className="num">{money(t.laborCost)}</td>
                    <td>{src('Labor cost')}</td>
                  </tr>
                  <tr><td>Products and expenses cost</td><td className="num">{money(t.costProducts)}</td><td>{src('Products & expenses cost')}</td></tr>
                  <tr className="pf-sub"><td>Total cost</td><td className="num">{money(t.totalCost)}</td><td /></tr>
                  <tr className="pf-sub"><td>Profit on posted items ({pct(t.postedMarginPct)} margin)</td><td className="num">{money(t.postedProfit)}</td><td /></tr>
                  {/* The headline figures run on what was invoiced, so the P&L
                      ends where they begin and the two can be read together. */}
                  <tr className="pf-section"><td colSpan={3}>Invoiced</td></tr>
                  <tr><td>Invoiced in this period (incl. tax, dated by invoice)</td><td className="num">{money(t.invoiced)}</td><td>{src('Invoiced (revenue)')}</td></tr>
                  <tr className="pf-total"><td>Profit on invoiced ({pct(t.marginPct)} margin)</td><td className="num">{money(t.profit)}</td><td>{src('Profit')}</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="card mb-4">
            <div className="card-head">
              <div className="grow">
                <h3>By month</h3>
                <p className="sub">
                  Posted is the billing items dated in the month; Invoiced is what went out on invoices dated in it. Profit and margin run on
                  invoiced, as the figures above do.
                </p>
              </div>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th className="num">Recurring</th>
                    <th className="num">Labor</th>
                    <th className="num">Products</th>
                    <th className="num">Posted</th>
                    <th className="num">Invoiced</th>
                    <th className="num">Hours</th>
                    <th className="num">Labor cost</th>
                    <th className="num">Other cost</th>
                    <th className="num">Profit</th>
                    <th className="num">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {d.months.map((m) => (
                    <tr key={m.month}>
                      <td>{monthName(m.month)}</td>
                      <td className="num">{money(m.recurring)}</td>
                      <td className="num">{money(m.labor)}</td>
                      <td className="num">{money(m.products)}</td>
                      <td className="num">{money(m.revenue)}</td>
                      <td className="num strong">{money(m.invoiced)}</td>
                      <td className="num">{num(m.hours, 1)}</td>
                      <td className="num">{money(m.laborCost)}</td>
                      <td className="num">{money(m.otherCost)}</td>
                      <td className={`num strong${m.profit < 0 ? ' pf-neg' : ''}`}>{money(m.profit)}</td>
                      <td className="num">{pct(m.marginPct)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className="pf-split mb-4">
            <section className="card">
              <div className="card-head">
                <div className="grow">
                  <h3>Labor draw by technician</h3>
                  <p className="sub">Hours worked by each technician.</p>
                </div>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Technician</th>
                      <th className="num">Hours</th>
                      <th className="num">Billable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.people.map((p) => (
                      <tr key={p.name}>
                        <td>{p.name}</td>
                        <td className="num">{num(p.hours, 1)}</td>
                        <td className="num">{num(p.billableHours, 1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="card">
              <div className="card-head">
                <div className="grow">
                  <h3>Labor draw by contract</h3>
                  <p className="sub">Which contract the time was logged against.</p>
                </div>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Contract</th>
                      <th>Type</th>
                      <th className="num">Hours</th>
                      <th className="num">Billable</th>
                      <th className="num">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.contracts.map((c) => (
                      <tr key={c.name}>
                        <td>{c.name}</td>
                        <td className="xs">{c.type ?? '—'}</td>
                        <td className="num">{num(c.hours, 1)}</td>
                        <td className="num">{num(c.billableHours, 1)}</td>
                        <td className="num">{money(c.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>

          <section className="card mb-4">
            <div className="card-head">
              <div className="grow">
                <h3>Labor draw by work type</h3>
                <p className="sub">The same hours and labor cost, split by the work type each time entry was logged as.</p>
              </div>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Work type</th>
                    <th className="num">Hours</th>
                    <th className="num">Share of hours</th>
                    <th className="num">Billable</th>
                    <th className="num">Cost</th>
                    <th className="num">Share of cost</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.workTypes ?? []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="muted small">
                        No time logged in this period.
                      </td>
                    </tr>
                  )}
                  {(d.workTypes ?? []).map((w) => (
                    <tr key={w.name}>
                      <td>
                        {w.name}
                        {w.uncostedHours > 0 && <div className="xs muted">{num(w.uncostedHours, 1)} h not costed</div>}
                      </td>
                      <td className="num">{num(w.hours, 1)}</td>
                      <td className="num">{d.totals.hoursWorked ? `${((w.hours / d.totals.hoursWorked) * 100).toFixed(1)}%` : '—'}</td>
                      <td className="num">{num(w.billableHours, 1)}</td>
                      <td className="num">{money(w.cost)}</td>
                      <td className="num">{d.totals.laborCost ? `${((w.cost / d.totals.laborCost) * 100).toFixed(1)}%` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card mb-4" id="sources">
            <div className="card-head">
              <div className="grow">
                <h3>Where every number comes from</h3>
                <p className="sub">
                  The Autotask entity, the fields read, the filter applied, the formula and how many records were used — so you can check the
                  right fields were picked. Profit = invoiced − (labor cost + recurring cost + products and expenses cost).
                </p>
              </div>
            </div>
            <div className="table-wrap">
              <table className="table pf-sources">
                <thead>
                  <tr>
                    <th>Figure</th>
                    <th className="num">Value</th>
                    <th>Autotask entity</th>
                    <th>Fields</th>
                    <th>Filter</th>
                    <th>Formula</th>
                    <th className="num">Records</th>
                  </tr>
                </thead>
                <tbody>
                  {d.sources.map((s) => (
                    <tr key={s.figure} id={`src-${slugForFile(s.figure)}`}>
                      <td className="strong">
                        {s.figure}
                        {s.note && <div className="xs muted" style={{ fontWeight: 400 }}>{s.note}</div>}
                      </td>
                      <td className="num">{money(s.value)}</td>
                      <td className="mono xs">{s.entity}</td>
                      <td className="mono xs">{s.fields}</td>
                      <td className="xs">{s.filter}</td>
                      <td className="xs">{s.formula}</td>
                      <td className="num">{num(s.records)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* The headline figures, opened out                                     */
/* ------------------------------------------------------------------ */

type DrillKey = 'revenue' | 'hours' | 'cost' | 'profit' | 'perHour' | 'costPerHour' | 'draw' | 'recurring';
/** The top row of tiles — the detail opens under the row it was clicked in. */
const ROW1: DrillKey[] = ['revenue', 'hours', 'cost', 'profit'];

/**
 * A headline figure that opens the rows it was added up from. It keeps the
 * .stat look exactly and only adds the affordance (the shared .stat-pick),
 * because a tile that restyled on becoming clickable would read as a
 * different thing.
 */
function DrillStat({
  k,
  open,
  onPick,
  label,
  value,
  foot,
}: {
  k: DrillKey;
  open: DrillKey | null;
  onPick: (k: DrillKey) => void;
  label: string;
  value: string;
  foot: string;
}) {
  const on = open === k;
  return (
    <button type="button" className={`stat stat-pick pf-drill-stat${on ? ' is-active' : ''}`} aria-expanded={on} onClick={() => onPick(k)}>
      <span className="label">{label}</span>
      <div className="figure">{value}</div>
      <span className="foot">{foot}</span>
      <span className="pf-drill-cue" aria-hidden="true">{on ? 'Hide detail ▴' : 'Show detail ▾'}</span>
    </button>
  );
}

/** The rows behind one headline figure. */
function Drill({ k, d, onClose }: { k: DrillKey; d: Payload; onClose: () => void }) {
  const t = d.totals;
  const months = d.months.filter((m) => m.invoiced || m.hours || m.recurring || m.laborCost);
  const costedHours = t.hoursWorked - t.uncostedHours;
  let title = '';
  let lede = '';
  let table: ReactNode = null;
  let note: ReactNode = null;

  switch (k) {
    case 'revenue':
      title = 'Revenue — every invoice in this period';
      lede = 'Autotask invoices dated in the period and not voided. This is the revenue the profit and the per-hour figures run on.';
      note = (
        <>
          Posted billing items over the same dates came to {money(t.revenue)} — dated by item rather than by invoice, and without tax. That is the
          revenue the profit and loss below is split into recurring, labor and products.
        </>
      );
      table = (
        <table className="table">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Date</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {d.invoiceList.length === 0 && (
              <tr>
                <td colSpan={3} className="muted small">
                  No invoices are dated in this period.
                </td>
              </tr>
            )}
            {d.invoiceList.map((i) => (
              <tr key={`${i.number}-${i.date}`}>
                <td className="mono xs">{i.number}</td>
                <td>{i.date ? day(i.date) : '—'}</td>
                <td className="num">{money(i.total)}</td>
              </tr>
            ))}
            <tr className="cr-total">
              <td colSpan={2}>
                {num(t.invoices)} {t.invoices === 1 ? 'invoice' : 'invoices'}
              </td>
              <td className="num">{money(t.invoiced)}</td>
            </tr>
          </tbody>
        </table>
      );
      break;

    case 'hours':
      title = 'Labor draw — who worked the hours';
      lede =
        "Every time entry in the period on this client's tickets, on tasks of its projects, and on its contracts with neither — posted or not, billable or not, because covered time costs us whether or not it is ever billed.";
      table = (
        <table className="table">
          <thead>
            <tr>
              <th>Technician</th>
              <th className="num">Hours</th>
              <th className="num">Billable</th>
              <th className="num">Share of hours</th>
              <th className="num">Cost</th>
            </tr>
          </thead>
          <tbody>
            {d.people.map((p) => (
              <tr key={p.name}>
                <td>{p.name}</td>
                <td className="num">{num(p.hours, 1)}</td>
                <td className="num">{num(p.billableHours, 1)}</td>
                <td className="num">{t.hoursWorked ? `${((p.hours / t.hoursWorked) * 100).toFixed(1)}%` : '—'}</td>
                <td className="num">{p.cost === null ? <span className="pf-neg">not costed</span> : money(p.cost)}</td>
              </tr>
            ))}
            <tr className="cr-total">
              <td>{num(d.people.length)} technicians</td>
              <td className="num">{num(t.hoursWorked, 1)}</td>
              <td className="num">{num(t.billableHours, 1)}</td>
              <td className="num">100%</td>
              <td className="num">{money(t.laborCost)}</td>
            </tr>
          </tbody>
        </table>
      );
      break;

    case 'cost':
      title = 'Total cost — what it all cost us';
      lede = "Labor at each technician's internal hourly cost, plus what Autotask posted as our cost on the recurring and product lines.";
      note =
        t.uncostedHours > 0 ? (
          <>
            {num(t.uncostedHours, 1)} hours have no internal cost in Autotask and are not in this total, so the cost is understated and the profit
            overstated by those hours.
          </>
        ) : null;
      table = (
        <table className="table">
          <thead>
            <tr>
              <th>Cost</th>
              <th>How it is worked out</th>
              <th className="num">Amount</th>
              <th className="num">Share</th>
            </tr>
          </thead>
          <tbody>
            {[
              { label: 'Labor', how: `${num(t.hoursWorked, 1)} hours worked × each technician’s internal cost`, value: t.laborCost },
              { label: 'Recurring services', how: 'ourCost on the posted recurring billing items', value: t.costRecurring },
              { label: 'Products and expenses', how: 'ourCost on the posted product and expense items', value: t.costProducts },
            ].map((r) => (
              <tr key={r.label}>
                <td>{r.label}</td>
                <td className="xs muted">{r.how}</td>
                <td className="num">{money(r.value)}</td>
                <td className="num">{t.totalCost ? `${((r.value / t.totalCost) * 100).toFixed(1)}%` : '—'}</td>
              </tr>
            ))}
            <tr className="cr-total">
              <td>Total cost</td>
              <td />
              <td className="num">{money(t.totalCost)}</td>
              <td className="num">100%</td>
            </tr>
          </tbody>
        </table>
      );
      break;

    case 'profit':
      title = 'Profit — invoiced, less every cost';
      lede = 'What was billed out in the period, less the labor it drew and what the services and products cost us.';
      note = (
        <>
          On posted billing items instead ({money(t.revenue)}), the same costs would leave {money(t.postedProfit)} at {pct(t.postedMarginPct)} — that
          is the profit the profit and loss below adds up to.
        </>
      );
      table = (
        <table className="table">
          <tbody>
            <tr>
              <td>Invoiced</td>
              <td className="num">{money(t.invoiced)}</td>
            </tr>
            <tr>
              <td>Less labor cost</td>
              <td className="num">−{money(t.laborCost)}</td>
            </tr>
            <tr>
              <td>Less recurring services cost</td>
              <td className="num">−{money(t.costRecurring)}</td>
            </tr>
            <tr>
              <td>Less products and expenses cost</td>
              <td className="num">−{money(t.costProducts)}</td>
            </tr>
            <tr className="cr-total">
              <td>Profit ({pct(t.marginPct)} margin)</td>
              <td className={`num${t.profit < 0 ? ' pf-neg' : ''}`}>{money(t.profit)}</td>
            </tr>
          </tbody>
        </table>
      );
      break;

    case 'perHour':
      title = 'Revenue per hour worked';
      lede = 'Invoiced ÷ hours worked, month by month — what an hour of our time brought in, billable or not.';
      table = (
        <table className="table">
          <thead>
            <tr>
              <th>Month</th>
              <th className="num">Invoiced</th>
              <th className="num">Hours worked</th>
              <th className="num">Per hour</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.month}>
                <td>{monthName(m.month)}</td>
                <td className="num">{money(m.invoiced)}</td>
                <td className="num">{num(m.hours, 1)}</td>
                <td className="num">{m.hours ? money(m.invoiced / m.hours) : '—'}</td>
              </tr>
            ))}
            <tr className="cr-total">
              <td>Whole period</td>
              <td className="num">{money(t.invoiced)}</td>
              <td className="num">{num(t.hoursWorked, 1)}</td>
              <td className="num">{money(t.revenuePerHour)}</td>
            </tr>
          </tbody>
        </table>
      );
      break;

    case 'costPerHour':
      title = 'Labor cost per hour';
      lede = "Each technician's internal hourly cost in Autotask over the hours they worked. Hours with no cost are left out of the average.";
      table = (
        <table className="table">
          <thead>
            <tr>
              <th>Technician</th>
              <th className="num">Cost / hr</th>
              <th className="num">Hours</th>
              <th className="num">Cost</th>
            </tr>
          </thead>
          <tbody>
            {d.people.map((p) => (
              <tr key={p.name}>
                <td>{p.name}</td>
                <td className="num">{p.internalCost ? money(p.internalCost) : <span className="pf-neg">none set</span>}</td>
                <td className="num">{num(p.hours, 1)}</td>
                <td className="num">{p.cost === null ? '—' : money(p.cost)}</td>
              </tr>
            ))}
            <tr className="cr-total">
              <td>Average over costed hours</td>
              <td className="num">{money(t.laborCostPerHour)}</td>
              <td className="num">{num(costedHours, 1)}</td>
              <td className="num">{money(t.laborCost)}</td>
            </tr>
          </tbody>
        </table>
      );
      break;

    case 'draw':
      title = 'Labor draw on recurring';
      lede = 'The labor cost this client drew against the recurring revenue it is meant to be covered by — the higher the share, the less of the fee is left.';
      table = (
        <table className="table">
          <thead>
            <tr>
              <th>Month</th>
              <th className="num">Labor cost</th>
              <th className="num">Recurring revenue</th>
              <th className="num">Draw</th>
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.month}>
                <td>{monthName(m.month)}</td>
                <td className="num">{money(m.laborCost)}</td>
                <td className="num">{money(m.recurring)}</td>
                <td className="num">{m.recurring ? `${((m.laborCost / m.recurring) * 100).toFixed(1)}%` : '—'}</td>
              </tr>
            ))}
            <tr className="cr-total">
              <td>Whole period</td>
              <td className="num">{money(t.laborCost)}</td>
              <td className="num">{money(t.revenueRecurring)}</td>
              <td className="num">{pct(t.laborDrawPct)}</td>
            </tr>
          </tbody>
        </table>
      );
      break;

    case 'recurring': {
      const list = d.items?.recurring ?? [];
      title = 'Recurring revenue — the billing items behind it';
      lede = 'Posted recurring items: subscriptions, recurring services and bundles, setup fees and milestones, dated by item.';
      table = (
        <table className="table sdf-items">
          <thead>
            <tr>
              <th>Date</th>
              <th>Item</th>
              <th>Description</th>
              <th className="num">Qty</th>
              <th className="num">Amount</th>
              <th className="num">Our cost</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr>
                <td colSpan={6} className="muted small">
                  No recurring items are dated in this period.
                </td>
              </tr>
            )}
            {list.map((i, n) => (
              <tr key={n}>
                <td>{i.date ? day(i.date) : '—'}</td>
                <td>{i.name || '—'}</td>
                <td className="sdf-desc">{i.description}</td>
                <td className="num">{i.quantity === null ? '—' : num(i.quantity, 2)}</td>
                <td className="num">{money(i.price)}</td>
                <td className="num">{money(i.cost)}</td>
              </tr>
            ))}
            <tr className="cr-total">
              <td colSpan={4}>
                {num(list.length)} {list.length === 1 ? 'item' : 'items'}
              </td>
              <td className="num">{money(t.revenueRecurring)}</td>
              <td className="num">{money(t.costRecurring)}</td>
            </tr>
          </tbody>
        </table>
      );
      break;
    }
  }

  return (
    <section className="card mb-4 pf-drill">
      <div className="card-head">
        <div className="grow">
          <h3>{title}</h3>
          <p className="sub">{lede}</p>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="table-wrap pf-drill-scroll">{table}</div>
      {note && <p className="xs muted pf-drill-note">{note}</p>}
    </section>
  );
}

interface AllRow {
  id: number | null;
  name: string;
  revenue: number;
  posted: number;
  recurring: number;
  labor: number;
  products: number;
  hours: number;
  billableHours: number;
  laborCost: number;
  uncostedHours: number;
  otherCost: number;
  totalCost: number;
  profit: number;
  marginPct: number | null;
  /* From Account Management; only on the all-clients list. */
  accountManager?: string | null;
}
interface AllPayload {
  from: string;
  to: string;
  fetchedUtc: string;
  clients: AllRow[];
  inactive: AllRow;
  unassignedHours: number;
  counts: { billingItems: number; timeEntries: number; costMissing: number };
}

/**
 * One revenue line of the P&L, opening to the billing items behind it. Our
 * cost is shown where it counts toward cost; on labor it does not (labor is
 * costed from time), so it is left off there.
 */
function PlLine({ label, value, source, items, showCost }: { label: string; value: number; source: ReactNode; items?: PlItem[]; showCost?: boolean }) {
  const [open, setOpen] = useState(false);
  const list = items ?? [];
  const cost = list.reduce((n, i) => n + (i.cost ?? 0), 0);
  return (
    <>
      <tr className={open ? 'pf-open' : undefined}>
        <td>
          {list.length ? (
            <button type="button" className="sdf-expand" aria-expanded={open} onClick={() => setOpen(!open)}>
              <span className="sdf-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
              {label}
              <span className="xs muted">{num(list.length)} {list.length === 1 ? 'item' : 'items'}</span>
            </button>
          ) : (
            label
          )}
        </td>
        <td className="num">{money(value)}</td>
        <td>{source}</td>
      </tr>
      {open && (
        <tr className="pf-items-row">
          <td colSpan={3}>
            <table className="table sdf-items">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Item</th>
                  <th>Description</th>
                  <th className="num">Qty</th>
                  <th className="num">Amount</th>
                  {showCost && <th className="num">Our cost</th>}
                </tr>
              </thead>
              <tbody>
                {list.map((i, n) => (
                  <tr key={n}>
                    <td>{i.date ? day(i.date) : '—'}</td>
                    <td>{i.name || '—'}</td>
                    <td className="sdf-desc">{i.description}</td>
                    <td className="num">{i.quantity === null ? '—' : num(i.quantity, 2)}</td>
                    <td className="num">{money(i.price)}</td>
                    {showCost && <td className="num">{money(i.cost)}</td>}
                  </tr>
                ))}
                <tr className="sdf-subtotal">
                  <td colSpan={4}>{label}</td>
                  <td className="num">{money(value)}</td>
                  {showCost && <td className="num">{money(cost)}</td>}
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

const NO_AM = '(none)';

/* The line between red and green. */
const THRESHOLD = 50;

/** Margin as one coloured line: green above 50%, red at 50% or below. */
function MarginBar({ pct: p }: { pct: number | null }) {
  if (p === null) return <span className="xs muted">no revenue</span>;
  const good = p > THRESHOLD;
  const width = Math.max(2, Math.min(100, Math.abs(p)));
  return (
    <div className={`pf-bar ${good ? 'good' : 'bad'}`} title={`${p.toFixed(1)}% margin`}>
      <span className="pf-bar-track">
        <span className="pf-bar-fill" style={{ width: `${width}%` }} />
        <span className="pf-bar-mark" style={{ left: `${THRESHOLD}%` }} />
      </span>
      <span className="pf-bar-label">{p.toFixed(1)}%</span>
    </div>
  );
}

/**
 * Every active client at once (GET /reports/profitability/all): revenue,
 * hours, labor cost, other cost, profit and margin on one line each, the
 * margin as a red/green bar. Same rules as the one-client view.
 */
function AllClients({
  from,
  to,
  onRange,
  onOpen,
}: {
  from: string;
  to: string;
  onRange: (r: { from: string; to: string }) => void;
  onOpen: (c: { id: number; name: string }) => void;
}) {
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [search, setSearch] = useState('');
  const [hideEmpty, setHideEmpty] = useState(true);
  const [only, setOnly] = useState<'' | 'red' | 'green'>('');
  /* '' is everybody; NO_AM is clients with no account manager. */
  const [manager, setManager] = useState('');
  const [sortBy, setSortBy] = useState<'revenue' | 'profit' | 'margin' | 'marginAsc' | 'hours' | 'name' | 'accountManager'>('revenue');
  const data = useHeldResource<AllPayload>(`/reports/profitability/all?${new URLSearchParams({ from, to })}`);
  const d = data.data;

  /* Everything the filters leave, before the margin band is picked — so the
     green and red tiles keep counting the whole list while one is showing. */
  const base = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (d?.clients ?? [])
      .filter((r) => !hideEmpty || r.revenue !== 0 || r.posted !== 0 || r.hours !== 0)
      .filter((r) => !q || r.name.toLowerCase().includes(q))
      .filter((r) => !manager || (manager === NO_AM ? !r.accountManager : r.accountManager === manager));
  }, [d, search, hideEmpty, manager]);

  const rows = useMemo(() => {
    const m = (r: AllRow) => r.marginPct ?? -Infinity;
    return base
      .filter((r) => !only || (r.marginPct !== null && (only === 'green' ? r.marginPct > THRESHOLD : r.marginPct <= THRESHOLD)))
      .sort((a, b) => {
        switch (sortBy) {
          case 'name':
            return a.name.localeCompare(b.name);
          case 'accountManager':
            /* Grouped by account manager, unassigned last; biggest client first within each. */
            if (!a.accountManager !== !b.accountManager) return a.accountManager ? -1 : 1;
            return (a.accountManager ?? '').localeCompare(b.accountManager ?? '') || b.revenue - a.revenue;
          case 'profit':
            return b.profit - a.profit;
          case 'margin':
            return m(b) - m(a);
          case 'marginAsc':
            return m(a) - m(b);
          case 'hours':
            return b.hours - a.hours;
          default:
            return b.revenue - a.revenue;
        }
      });
  }, [base, only, sortBy]);

  /* Everyone who manages at least one listed client, with how many. */
  const managers = useMemo(() => {
    const n = new Map<string, number>();
    let none = 0;
    for (const r of d?.clients ?? []) {
      if (r.accountManager) n.set(r.accountManager, (n.get(r.accountManager) ?? 0) + 1);
      else none += 1;
    }
    return { list: [...n].sort((a, b) => a[0].localeCompare(b[0])), none };
  }, [d]);

  const total = rows.reduce(
    (t, r) => ({ revenue: t.revenue + r.revenue, hours: t.hours + r.hours, laborCost: t.laborCost + r.laborCost, otherCost: t.otherCost + r.otherCost, profit: t.profit + r.profit }),
    { revenue: 0, hours: 0, laborCost: 0, otherCost: 0, profit: 0 },
  );
  const totalMargin = total.revenue ? Math.round((total.profit / total.revenue) * 1000) / 10 : null;
  const green = base.filter((r) => r.marginPct !== null && r.marginPct > THRESHOLD).length;
  const red = base.filter((r) => r.marginPct !== null && r.marginPct <= THRESHOLD).length;

  const exportCsv = () =>
    downloadCsv(`client_profitability_all_${from}_to_${to}_${fileStamp()}.csv`, [
      ['Client', 'Account manager', 'Recurring', 'Labor billed', 'Products', 'Posted revenue', 'Revenue (invoiced)', 'Hours worked', 'Billable hours', 'Labor cost', 'Hours not costed', 'Other cost', 'Total cost', 'Profit', 'Margin %'],
      ...rows.map((r) => [r.name, r.accountManager ?? '', r.recurring, r.labor, r.products, r.posted, r.revenue, r.hours, r.billableHours, r.laborCost, r.uncostedHours, r.otherCost, r.totalCost, r.profit, r.marginPct ?? '']),
    ]);

  return (
    <>
      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="From">
              <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <input className="input" type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
            </Field>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!draftFrom || !draftTo || draftFrom > draftTo || (draftFrom === from && draftTo === to)}
              onClick={() => onRange({ from: draftFrom, to: draftTo })}
            >
              Show
            </button>
            <div className="row wrap" style={{ gap: 'var(--space-1)' }}>
              {QUICK.map((q) => (
                <button
                  key={q.k}
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const r = quick(q.k);
                    setDraftFrom(r.from);
                    setDraftTo(r.to);
                    onRange(r);
                  }}
                >
                  {q.label}
                </button>
              ))}
            </div>
          </div>
          <div className="row wrap mt-3" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Search">
              <input className="input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Client name" />
            </Field>
            <Field label="Show">
              <select className="select" value={only} onChange={(e) => setOnly(e.target.value as typeof only)}>
                <option value="">All margins</option>
                <option value="red">Red — 50% or below</option>
                <option value="green">Green — above 50%</option>
              </select>
            </Field>
            <Field label="Account manager">
              <select className="select" value={manager} onChange={(e) => setManager(e.target.value)}>
                <option value="">All account managers</option>
                {managers.list.map(([name, count]) => (
                  <option key={name} value={name}>
                    {name} ({count})
                  </option>
                ))}
                {managers.none > 0 && <option value={NO_AM}>No account manager ({managers.none})</option>}
              </select>
            </Field>
            <Field label="Sort by">
              <select className="select" value={sortBy} onChange={(e) => setSortBy(e.target.value as typeof sortBy)}>
                <option value="revenue">Revenue, highest first</option>
                <option value="profit">Profit, highest first</option>
                <option value="margin">Margin, highest first</option>
                <option value="marginAsc">Margin, lowest first</option>
                <option value="hours">Hours worked</option>
                <option value="name">Client name, A–Z</option>
                <option value="accountManager">Account manager, A–Z</option>
              </select>
            </Field>
            <label className="checkbox">
              <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />
              Hide clients with no revenue or time
            </label>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!rows.length} onClick={exportCsv}>
              Export CSV
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>
          <p className="xs muted mt-2">
            {day(from)} – {day(to)}. Revenue is what was invoiced in the period (incl. tax, dated by invoice) and profit is that less every cost;
            other cost from posted billing items; labor draw from every time entry, costed at each technician&apos;s internal cost — the same rules
            as one client.
            {d && ` Read ${num(d.counts.billingItems)} billing items and ${num(d.counts.timeEntries)} time entries.`}
            {d && d.unassignedHours > 0 && ` ${num(d.unassignedHours, 1)} hours were on no client (internal time) and are not counted.`}
            {d && d.inactive.revenue !== 0 && ` ${money(d.inactive.revenue)} of revenue belongs to inactive companies and is not listed.`}
          </p>
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && !d && <Loading label="Reading every client's billing and time from Autotask — this can take a minute" />}

      {d && (
        <>
          {/* The figures are the filter: clicking one narrows the list under
              it to the clients behind it, and clicking it again clears. */}
          <div className="grid grid-4 mb-4">
            <button
              type="button"
              className={`stat stat-pick${sortBy === 'revenue' ? ' is-active' : ''}`}
              aria-pressed={sortBy === 'revenue'}
              onClick={() => setSortBy('revenue')}
            >
              <span className="label">Revenue</span>
              <div className="figure">{money(total.revenue)}</div>
              <span className="foot">{num(rows.length)} clients shown · invoiced, sort by it</span>
            </button>
            <button
              type="button"
              className={`stat stat-pick${sortBy === 'profit' ? ' is-active' : ''}`}
              aria-pressed={sortBy === 'profit'}
              onClick={() => setSortBy('profit')}
            >
              <span className="label">Profit</span>
              <div className="figure">{money(total.profit)}</div>
              <span className="foot">{totalMargin === null ? '—' : `${totalMargin.toFixed(1)}%`} margin overall · sort by it</span>
            </button>
            <button
              type="button"
              className={`stat stat-pick${only === 'green' ? ' is-active' : ''}`}
              aria-pressed={only === 'green'}
              onClick={() => setOnly(only === 'green' ? '' : 'green')}
            >
              <span className="label">Above 50% margin</span>
              <div className="figure">{num(green)}</div>
              <span className="foot">{only === 'green' ? 'showing these only — click to clear' : 'clients in green · show only these'}</span>
            </button>
            <button
              type="button"
              className={`stat stat-pick${only === 'red' ? ' is-active' : ''}`}
              aria-pressed={only === 'red'}
              onClick={() => setOnly(only === 'red' ? '' : 'red')}
            >
              <span className="label">50% or below</span>
              <div className="figure">{num(red)}</div>
              <span className="foot">{only === 'red' ? 'showing these only — click to clear' : 'clients in red · show only these'}</span>
            </button>
          </div>
          <section className="card">
            <div className="table-wrap">
              <table className="table pf-all" data-nosort>
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Account manager</th>
                    <th className="num">Revenue</th>
                    <th className="num">Hours</th>
                    <th className="num">Labor cost</th>
                    <th className="num">Other cost</th>
                    <th className="num">Profit</th>
                    <th style={{ width: 240 }}>Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="muted small">
                        No clients match.
                      </td>
                    </tr>
                  )}
                  {rows.map((r) => (
                    <tr key={r.id ?? r.name}>
                      <td>
                        <button type="button" className="link-button" onClick={() => r.id && onOpen({ id: r.id, name: r.name })}>
                          {r.name}
                        </button>
                        {r.uncostedHours > 0 && <div className="xs muted">{num(r.uncostedHours, 1)} h not costed</div>}
                      </td>
                      <td className={r.accountManager ? undefined : 'muted'}>{r.accountManager ?? '—'}</td>
                      <td className="num">{money(r.revenue)}</td>
                      <td className="num">{num(r.hours, 1)}</td>
                      <td className="num">{money(r.laborCost)}</td>
                      <td className="num">{money(r.otherCost)}</td>
                      <td className={`num strong${r.profit < 0 ? ' pf-neg' : ''}`}>{money(r.profit)}</td>
                      <td>
                        <MarginBar pct={r.marginPct} />
                      </td>
                    </tr>
                  ))}
                  {rows.length > 1 && (
                    <tr className="cr-total">
                      <td>{num(rows.length)} clients</td>
                      <td />
                      <td className="num">{money(total.revenue)}</td>
                      <td className="num">{num(total.hours, 1)}</td>
                      <td className="num">{money(total.laborCost)}</td>
                      <td className="num">{money(total.otherCost)}</td>
                      <td className="num">{money(total.profit)}</td>
                      <td>
                        <MarginBar pct={totalMargin} />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}
